import { getSupabase, isSupabaseConfigured } from './supabase-client';
import { getOAuthRedirectUrl, isNativeApp } from './platform';

const OAUTH_MODE_KEY = 'fb_oauth_mode';
const OAUTH_PROVIDER_KEY = 'fb_oauth_provider';
const OAUTH_SIGNUP_SESSION_KEY = 'fb_oauth_signup_session';
const PKCE_BACKUP_KEY = 'fb_pkce_backup';

/**
 * Back up all Supabase localStorage entries (including the PKCE code verifier)
 * to sessionStorage so they survive iOS WebView localStorage clearing.
 */
function backupPkceState() {
  try {
    const backup = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-')) {
        backup[key] = localStorage.getItem(key);
      }
    }
    sessionStorage.setItem(PKCE_BACKUP_KEY, JSON.stringify(backup));
  } catch (_) { /* ignore */ }
}

/**
 * Restore Supabase localStorage entries from the sessionStorage backup.
 * Called before exchangeCodeForSession to ensure the PKCE verifier is present.
 */
function restorePkceState() {
  try {
    const raw = sessionStorage.getItem(PKCE_BACKUP_KEY);
    if (!raw) return;
    const backup = JSON.parse(raw);
    for (const [key, value] of Object.entries(backup)) {
      if (localStorage.getItem(key) == null) {
        localStorage.setItem(key, value);
      }
    }
    sessionStorage.removeItem(PKCE_BACKUP_KEY);
  } catch (_) { /* ignore */ }
}

export function storeOAuthContext(mode, provider) {
  try {
    sessionStorage.setItem(OAUTH_MODE_KEY, mode);
    sessionStorage.setItem(OAUTH_PROVIDER_KEY, provider);
  } catch (_) { /* ignore */ }
}

export function readOAuthContext() {
  try {
    return {
      mode: sessionStorage.getItem(OAUTH_MODE_KEY) || 'login',
      provider: sessionStorage.getItem(OAUTH_PROVIDER_KEY) || 'google',
    };
  } catch (_) {
    return { mode: 'login', provider: 'google' };
  }
}

export function clearOAuthContext() {
  try {
    sessionStorage.removeItem(OAUTH_MODE_KEY);
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
  } catch (_) { /* ignore */ }
}

export function storeOAuthSignupSession(payload) {
  try {
    sessionStorage.setItem(OAUTH_SIGNUP_SESSION_KEY, JSON.stringify(payload));
  } catch (_) { /* ignore */ }
}

export function readOAuthSignupSession() {
  try {
    const raw = sessionStorage.getItem(OAUTH_SIGNUP_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function clearOAuthSignupSession() {
  try {
    sessionStorage.removeItem(OAUTH_SIGNUP_SESSION_KEY);
  } catch (_) { /* ignore */ }
}

export async function startOAuth(provider, mode = 'login') {
  const sb = getSupabase();
  if (!sb || !isSupabaseConfigured()) {
    return { error: 'Google/Apple sign-in is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_KEY.' };
  }

  storeOAuthContext(mode, provider);
  const redirectTo = getOAuthRedirectUrl();
  const skipBrowserRedirect = isNativeApp();

  const { data, error } = await sb.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
      skipBrowserRedirect,
    },
  });

  if (error) return { error: error.message };

  if (skipBrowserRedirect && data?.url) {
    // Back up PKCE code verifier before opening external browser.
    // iOS may clear WebView localStorage while SFSafariViewController is open.
    backupPkceState();
    try {
      const { Browser } = await import('@capacitor/browser');
      // 'fullscreen' required on iPad — 'popover' needs a source anchor
      // that Capacitor Browser cannot provide, causing a UIKit crash.
      await Browser.open({ url: data.url, presentationStyle: 'fullscreen' });
      return { opened: true };
    } catch (e) {
      return { error: e.message || 'Could not open sign-in browser' };
    }
  }

  return { redirecting: true };
}

function parseCallbackParams(url) {
  const raw = String(url || '');
  const queryStart = raw.indexOf('?');
  const hashStart = raw.indexOf('#');
  const query = queryStart >= 0
    ? raw.slice(queryStart + 1, hashStart >= 0 ? hashStart : undefined)
    : '';
  const hash = hashStart >= 0 ? raw.slice(hashStart + 1) : '';
  const params = new URLSearchParams(query);
  const hashParams = new URLSearchParams(hash);
  return {
    code: params.get('code') || hashParams.get('code'),
    accessToken: hashParams.get('access_token'),
    refreshToken: hashParams.get('refresh_token'),
    error: params.get('error_description') || params.get('error') || hashParams.get('error_description') || hashParams.get('error'),
  };
}

export async function finishOAuthFromUrl(callbackUrl) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase is not configured');

  const targetUrl = callbackUrl || window.location.href;
  const { code, accessToken, refreshToken, error } = parseCallbackParams(targetUrl);
  if (error) throw new Error(error);

  // Restore PKCE code verifier from backup in case iOS cleared localStorage
  // while SFSafariViewController was open.
  if (isNativeApp()) restorePkceState();

  let session = null;

  if (code) {
    const { data, error: err } = await sb.auth.exchangeCodeForSession(code);
    if (err) throw err;
    session = data.session;
  } else if (accessToken) {
    session = {
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_in: 3600,
    };
  } else {
    const { data } = await sb.auth.getSession();
    session = data?.session || null;
  }

  if (!session?.access_token) {
    throw new Error('Sign-in did not return a session. Try again.');
  }

  if (isNativeApp()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.close();
    } catch (_) { /* ignore */ }
  }

  return session;
}

export async function getLaunchUrl() {
  if (!isNativeApp()) return null;
  const { App } = await import('@capacitor/app');
  const launch = await App.getLaunchUrl();
  return launch?.url || null;
}

export async function registerNativeDeepLinkListener(onCallback) {
  if (!isNativeApp()) return () => {};
  const { App } = await import('@capacitor/app');
  const sub = await App.addListener('appUrlOpen', (event) => {
    if (event?.url) {
      onCallback(event.url);
    }
  });
  return () => sub.remove();
}

/** @deprecated Use registerNativeDeepLinkListener */
export async function registerNativeOAuthListener(onCallback) {
  return registerNativeDeepLinkListener((url) => {
    if (url.includes('auth/callback')) {
      onCallback(url);
    }
  });
}
